import React, { useEffect, useRef, useState } from 'react';
import { getFuncionarios, getDepartamentos, createFuncionario, updateFuncionario, deleteFuncionario } from '../services/api';
import { getJornadas } from '../services/api';

const MEDIA = 'http://localhost:8000';

const DIAS_ORD = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM'];
const DIAS_SHORT = { SEG: 'Seg', TER: 'Ter', QUA: 'Qua', QUI: 'Qui', SEX: 'Sex', SAB: 'Sáb', DOM: 'Dom' };

const toHHMM = h => {
  if (!h) return '00:00';
  const hh = Math.floor(h), mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const calcDia = d => {
  const toMin = t => { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const e = toMin(d.entrada), s = toMin(d.saida);
  if (!e || !s || s <= e) return 0;
  let tot = s - e;
  const ia = toMin(d.inicio_almoco), fa = toMin(d.fim_almoco);
  if (ia && fa && fa > ia) tot -= (fa - ia);
  return tot / 60;
};

const jornadaLabel = j => {
  const hd = j.horarios_dias || {};
  const dias = DIAS_ORD.filter(d => hd[d]);
  const totalSem = dias.reduce((s, d) => s + calcDia(hd[d]), 0);

  let diasStr;
  if (dias.length === 5 && JSON.stringify(dias) === JSON.stringify(['SEG','TER','QUA','QUI','SEX']))
    diasStr = 'Seg–Sex';
  else if (dias.length === 6 && !dias.includes('DOM'))
    diasStr = 'Seg–Sáb';
  else if (dias.length === 7)
    diasStr = 'Seg–Dom';
  else if (dias.length === 0)
    diasStr = '';
  else
    diasStr = `${DIAS_SHORT[dias[0]]}–${DIAS_SHORT[dias[dias.length - 1]]}`;

  const semStr = totalSem > 0 ? `${toHHMM(totalSem)}/sem` : `${toHHMM(parseFloat(j.carga_horaria) * (dias.length || 5))}/sem`;
  return diasStr ? `${j.nome}  ·  ${diasStr}  ·  ${semStr}` : j.nome;
};

const FORM0 = {
  matricula: '', nome: '', cpf: '', cargo: '', departamento: '',
  jornada: '', email: '', telefone: '',
  data_nascimento: '', data_admissao: '', ativo: true,
};

const initials = n => (n || '').split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();

// ── Validações ──────────────────────────────────────────
const cpfValido = cpf => {
  const n = cpf.replace(/\D/g, '');
  if (n.length !== 11 || /^(\d)\1+$/.test(n)) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += +n[i] * (10 - i);
  let r = (s * 10) % 11; if (r === 10 || r === 11) r = 0;
  if (r !== +n[9]) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += +n[i] * (11 - i);
  r = (s * 10) % 11; if (r === 10 || r === 11) r = 0;
  return r === +n[10];
};

const cpfMask = v => {
  const n = v.replace(/\D/g, '').slice(0, 11);
  return n
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const telMask = v => {
  const n = v.replace(/\D/g, '').slice(0, 11);
  if (n.length <= 10)
    return n.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
  return n.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
};

const validar = (form) => {
  const erros = {};
  if (!form.matricula.trim()) erros.matricula = 'Obrigatório';
  if (!form.nome.trim()) erros.nome = 'Obrigatório';
  if (form.cpf && !cpfValido(form.cpf)) erros.cpf = 'CPF inválido';
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    erros.email = 'E-mail inválido';
  if (form.data_nascimento && form.data_admissao) {
    if (new Date(form.data_nascimento) >= new Date(form.data_admissao))
      erros.data_admissao = 'Admissão deve ser posterior ao nascimento';
  }
  return erros;
};

export default function Funcionarios() {
  const [lista, setLista] = useState([]);
  const [deptos, setDeptos] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [filtros, setFiltros] = useState({ nome: '', matricula: '', departamento: '', ativo: 'true' });
  const [form, setForm] = useState(FORM0);
  const [errosForm, setErrosForm] = useState({});
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [editId, setEditId] = useState(null);
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erroApi, setErroApi] = useState('');
  const fileRef = useRef();

  useEffect(() => {
    getDepartamentos({ ativo: true }).then(r => setDeptos(r.data.results || r.data));
    getJornadas({ ativo: true }).then(r => setJornadas(r.data.results || r.data));
  }, []);

  useEffect(() => { carregar(); }, [filtros]); // eslint-disable-line

  const carregar = async () => {
    setLoading(true);
    try { const r = await getFuncionarios(filtros); setLista(r.data.results || r.data); }
    finally { setLoading(false); }
  };

  const abrir = (f = null) => {
    if (f) {
      setForm({
        matricula: f.matricula, nome: f.nome, cpf: f.cpf || '',
        cargo: f.cargo || '', departamento: f.departamento || '',
        jornada: f.jornada || '',
        email: f.email || '', telefone: f.telefone || '',
        data_nascimento: f.data_nascimento || '',
        data_admissao: f.data_admissao || '',
        ativo: f.ativo,
      });
      setFotoPreview(f.foto ? `${MEDIA}${f.foto}` : null);
      setEditId(f.id);
    } else {
      setForm(FORM0);
      setFotoPreview(null);
      setEditId(null);
    }
    setFotoFile(null); setErrosForm({}); setErroApi(''); setModal(true);
  };

  const onFotoChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    setFotoFile(file);
    setFotoPreview(URL.createObjectURL(file));
  };

  const salvar = async (e) => {
    e.preventDefault();
    const erros = validar(form);
    if (Object.keys(erros).length) { setErrosForm(erros); return; }
    setLoading(true);
    try {
      let payload;
      if (fotoFile) {
        payload = new FormData();
        Object.entries(form).forEach(([k, v]) => {
          if (v !== '' && v !== null && v !== undefined) payload.append(k, v);
        });
        payload.append('foto', fotoFile);
      } else {
        payload = {
          ...form,
          departamento: form.departamento || null,
          jornada: form.jornada || null,
          data_admissao: form.data_admissao || null,
          data_nascimento: form.data_nascimento || null,
        };
      }
      editId ? await updateFuncionario(editId, payload) : await createFuncionario(payload);
      setModal(false); carregar();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        // mapear erros de campo do backend
        const mapped = {};
        if (data.matricula) mapped.matricula = data.matricula.join(' ');
        if (data.cpf) mapped.cpf = data.cpf.join(' ');
        if (data.email) mapped.email = data.email.join(' ');
        if (Object.keys(mapped).length) { setErrosForm(mapped); }
        else { setErroApi(JSON.stringify(data)); }
      } else { setErroApi('Erro ao salvar'); }
    } finally { setLoading(false); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este funcionário?')) return;
    await deleteFuncionario(id); carregar();
  };

  const campo = (nome) => ({
    className: `form-control${errosForm[nome] ? ' is-invalid' : ''}`,
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16, marginBottom: 20, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 38, height: 38, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1rem', marginRight: 14, flexShrink: 0 }}>
            <i className="bi bi-people-fill" />
          </div>
          <div>
            <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Gestão de Pessoal · Cadastros</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', letterSpacing: '-0.5px', lineHeight: 1.2 }}>Funcionários</div>
          </div>
        </div>
        <button className="btn-primary-custom" onClick={() => abrir()}>
          <i className="bi bi-plus-lg" />Novo
        </button>
      </div>

      <div className="filter-bar">
        <div style={{ flex: 2, minWidth: 160 }}>
          <label className="form-label">Nome</label>
          <input className="form-control" placeholder="Buscar..." value={filtros.nome} onChange={e => setFiltros({ ...filtros, nome: e.target.value })} />
        </div>
        <div style={{ minWidth: 110 }}>
          <label className="form-label">Matrícula</label>
          <input className="form-control" value={filtros.matricula} onChange={e => setFiltros({ ...filtros, matricula: e.target.value })} />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <label className="form-label">Departamento</label>
          <select className="form-select" value={filtros.departamento} onChange={e => setFiltros({ ...filtros, departamento: e.target.value })}>
            <option value="">Todos</option>
            {deptos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 110 }}>
          <label className="form-label">Status</label>
          <select className="form-select" value={filtros.ativo} onChange={e => setFiltros({ ...filtros, ativo: e.target.value })}>
            <option value="">Todos</option>
            <option value="true">Ativos</option>
            <option value="false">Inativos</option>
          </select>
        </div>
      </div>

      <div className="card-ponto">
        <div className="table-responsive">
          <table className="table-ponto" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ paddingLeft: 32 }}>Funcionário</th>
                <th>Nascimento</th>
                <th>Cargo</th>
                <th>Jornada</th>
                <th>Departamento</th>
                <th>E-mail</th>
                <th>Status</th>
                <th style={{ width: 80 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-3"><span className="spinner-border spinner-border-sm text-primary" /></td></tr>}
              {!loading && lista.map(f => (
                <tr key={f.id}>
                  <td style={{ maxWidth: 240, paddingLeft: 32 }}>
                    <div className="d-flex align-items-center gap-2">
                      {f.foto
                        ? <img src={`${MEDIA}${f.foto}`} alt={f.nome} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                        : <div className="avatar-initials" style={{ width: 32, height: 32, fontSize: 'var(--fs-xs)' }}>{initials(f.nome)}</div>
                      }
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600 }}>{f.nome}</span>
                        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>#{f.matricula}</span>
                      </div>
                    </div>
                  </td>
                  <td style={{ color: 'var(--text-muted)' }}>{f.data_nascimento || '—'}</td>
                  <td>{f.cargo || '—'}</td>
                  <td>{f.jornada_nome
                    ? <span style={{ background: '#f0fdf4', color: '#166534', borderRadius: 3, padding: '1px 7px', fontSize: 'var(--fs-xs)', fontWeight: 600 }}>{f.jornada_nome}</span>
                    : '—'}
                  </td>
                  <td>{f.departamento_nome
                    ? <span style={{ background: '#eff6ff', color: 'var(--blue)', borderRadius: 3, padding: '1px 7px', fontSize: 'var(--fs-xs)', fontWeight: 600 }}>{f.departamento_nome}</span>
                    : '—'}
                  </td>
                  <td style={{ color: 'var(--text-muted)' }}>{f.email || '—'}</td>
                  <td>
                    <span style={{ background: f.ativo ? '#dcfce7' : '#fee2e2', color: f.ativo ? '#166534' : '#991b1b', borderRadius: 3, padding: '1px 8px', fontSize: 'var(--fs-xs)', fontWeight: 700 }}>
                      {f.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>
                    <button className="btn-icon" style={{ color: '#1d4ed8', marginRight: 6 }} onClick={() => abrir(f)}><i className="bi bi-pencil" /></button>
                    <button className="btn-icon" style={{ color: '#dc2626' }} onClick={() => excluir(f.id)}><i className="bi bi-trash" /></button>
                  </td>
                </tr>
              ))}
              {!loading && lista.length === 0 && <tr><td colSpan={8}><div className="empty-state"><i className="bi bi-people" /><p>Nenhum funcionário</p></div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal-box" style={{ maxWidth: 660 }}>
            <div className="modal-header-custom">
              <h5><i className="bi bi-person me-2" />{editId ? 'Editar Funcionário' : 'Novo Funcionário'}</h5>
              <button className="modal-close" onClick={() => setModal(false)}><i className="bi bi-x-lg" /></button>
            </div>
            <form onSubmit={salvar} noValidate>
              <div className="modal-body-custom">
                {erroApi && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '8px 12px', color: '#991b1b', fontSize: 'var(--fs-xs)', marginBottom: 12 }}>{erroApi}</div>}

                {/* Foto */}
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div onClick={() => fileRef.current.click()} style={{ width: 72, height: 72, borderRadius: '50%', border: '2px dashed var(--border)', cursor: 'pointer', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gray-50)' }}>
                    {fotoPreview
                      ? <img src={fotoPreview} alt="foto" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <i className="bi bi-camera" style={{ fontSize: '1.4rem', color: 'var(--text-muted)' }} />
                    }
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>Foto do funcionário</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 4 }}>Clique na imagem para selecionar. JPG ou PNG.</div>
                    <button type="button" className="btn-secondary-custom" onClick={() => fileRef.current.click()}>
                      <i className="bi bi-upload" />Escolher arquivo
                    </button>
                    {fotoPreview && (
                      <button type="button" className="btn-danger-custom" style={{ marginLeft: 6 }} onClick={() => { setFotoFile(null); setFotoPreview(null); }}>
                        <i className="bi bi-x" />Remover
                      </button>
                    )}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFotoChange} />
                </div>

                <div className="row g-2">
                  {/* Matrícula */}
                  <div className="col-md-3">
                    <label className="form-label">Matrícula <span style={{ color: '#dc2626' }}>*</span></label>
                    <input {...campo('matricula')} value={form.matricula} onChange={e => { setForm({ ...form, matricula: e.target.value }); setErrosForm(p => ({ ...p, matricula: '' })); }} />
                    {errosForm.matricula && <div className="invalid-feedback">{errosForm.matricula}</div>}
                  </div>
                  {/* Nome */}
                  <div className="col-md-9">
                    <label className="form-label">Nome Completo <span style={{ color: '#dc2626' }}>*</span></label>
                    <input {...campo('nome')} value={form.nome} onChange={e => { setForm({ ...form, nome: e.target.value }); setErrosForm(p => ({ ...p, nome: '' })); }} />
                    {errosForm.nome && <div className="invalid-feedback">{errosForm.nome}</div>}
                  </div>
                  {/* CPF */}
                  <div className="col-md-4">
                    <label className="form-label">CPF</label>
                    <input {...campo('cpf')} placeholder="000.000.000-00" value={form.cpf}
                      onChange={e => { setForm({ ...form, cpf: cpfMask(e.target.value) }); setErrosForm(p => ({ ...p, cpf: '' })); }} />
                    {errosForm.cpf && <div className="invalid-feedback">{errosForm.cpf}</div>}
                  </div>
                  {/* Nascimento */}
                  <div className="col-md-4">
                    <label className="form-label">Data de Nascimento</label>
                    <input type="date" {...campo('data_nascimento')} value={form.data_nascimento} onChange={e => { setForm({ ...form, data_nascimento: e.target.value }); setErrosForm(p => ({ ...p, data_nascimento: '' })); }} />
                    {errosForm.data_nascimento && <div className="invalid-feedback">{errosForm.data_nascimento}</div>}
                  </div>
                  {/* Admissão */}
                  <div className="col-md-4">
                    <label className="form-label">Admissão</label>
                    <input type="date" {...campo('data_admissao')} value={form.data_admissao} onChange={e => { setForm({ ...form, data_admissao: e.target.value }); setErrosForm(p => ({ ...p, data_admissao: '' })); }} />
                    {errosForm.data_admissao && <div className="invalid-feedback">{errosForm.data_admissao}</div>}
                  </div>
                  {/* Cargo */}
                  <div className="col-md-5">
                    <label className="form-label">Cargo</label>
                    <input className="form-control" value={form.cargo} onChange={e => setForm({ ...form, cargo: e.target.value })} />
                  </div>
                  {/* Departamento */}
                  <div className="col-md-7">
                    <label className="form-label">Departamento</label>
                    <select className="form-select" value={form.departamento} onChange={e => setForm({ ...form, departamento: e.target.value })}>
                      <option value="">—</option>
                      {deptos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
                    </select>
                  </div>
                  {/* Jornada */}
                  <div className="col-12">
                    <label className="form-label">Jornada de Trabalho</label>
                    <select className="form-select" value={form.jornada} onChange={e => setForm({ ...form, jornada: e.target.value })}>
                      <option value="">— Não definida —</option>
                      {jornadas.map(j => (
                        <option key={j.id} value={j.id}>{jornadaLabel(j)}</option>
                      ))}
                    </select>
                  </div>
                  {/* E-mail */}
                  <div className="col-md-6">
                    <label className="form-label">E-mail</label>
                    <input {...campo('email')} type="email" value={form.email} onChange={e => { setForm({ ...form, email: e.target.value }); setErrosForm(p => ({ ...p, email: '' })); }} />
                    {errosForm.email && <div className="invalid-feedback">{errosForm.email}</div>}
                  </div>
                  {/* Telefone */}
                  <div className="col-md-6">
                    <label className="form-label">Telefone</label>
                    <input className="form-control" placeholder="(00) 00000-0000" value={form.telefone}
                      onChange={e => setForm({ ...form, telefone: telMask(e.target.value) })} />
                  </div>
                  {/* Ativo */}
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="ativo-chk" checked={form.ativo} onChange={e => setForm({ ...form, ativo: e.target.checked })} />
                      <label className="form-check-label" htmlFor="ativo-chk" style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>Funcionário ativo</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer-custom">
                <button type="button" className="btn-secondary-custom" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary-custom" disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg" />Salvar</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
